import React, { useState } from 'react';
import { CheckIcon, InfinityIcon, GiftIcon } from 'lucide-react';
import { QuantityStepper, money } from './QuantityStepper';
import { GoldMedal } from './LifetimeSeal';
import { PrescriptionBadge } from './PrescriptionBadge';
import { offers, Offer } from '../data/product';
import { Link } from 'react-router-dom';
import { CompanyPack } from './CompanyPack';
import { IncludedGear } from './IncludedGear';
import { FreeUnitModal } from './FreeUnitModal';
import { PaymentLogos } from './PaymentLogos';
import { UspsBadge } from './UspsBadge';
import { SecureCheckoutNote } from './SecureCheckoutNote';
import { trackCustom, packEvents } from '../utils/tracking';
interface OfferSectionProps {
  selectedOffer: Offer['id'];
  onSelectOffer: (id: Offer['id']) => void;
}
const packMath: Record<Offer['id'], {
  price: number;
  units: number;
  compareAt?: number;
  save?: number;
}> = {
  single: {
    price: 39.95,
    units: 1
  },
  trio: {
    price: 74.95,
    units: 3,
    compareAt: 119.85,
    save: 44.9
  },
  crew: {
    price: 119.85,
    units: 5,
    compareAt: 199.75,
    save: 79.9
  }
};
export function OfferSection({
  selectedOffer,
  onSelectOffer
}: OfferSectionProps) {
  const [quantities, setQuantities] = useState<Record<Offer['id'], number>>({
    single: 1,
    trio: 1,
    crew: 1
  });
  const [freeUnitOpen, setFreeUnitOpen] = useState(false);
  const [freeUnitDeclined, setFreeUnitDeclined] = useState(false);
  const handleQuantity = (id: Offer['id'], next: number) => {
    // Two singles cost exactly what the 3-pack costs, so surface the free unit
    // instead of letting anyone overpay for the same money.
    if (id === 'single' && next >= 2 && !freeUnitDeclined) {
      setFreeUnitOpen(true);
      return;
    }
    setQuantities(prev => ({
      ...prev,
      [id]: next
    }));
  };
  const acceptFreeUnit = () => {
    setFreeUnitOpen(false);
    setQuantities(prev => ({
      ...prev,
      single: 1,
      trio: 1
    }));
    onSelectOffer('trio');
    // Land on the 3-pack's buy button so the next action is right under the eye.
    const target = document.getElementById('pack-trio-cta');
    if (target) target.scrollIntoView({
      behavior: 'smooth',
      block: 'end'
    });
  };
  const declineFreeUnit = () => {
    setFreeUnitOpen(false);
    setFreeUnitDeclined(true);
    setQuantities(prev => ({
      ...prev,
      single: 2
    }));
  };
  return <section id="offer" className="border-b border-white/10 bg-ink-900" data-id="element-533" data-mp="3ouddbv2keqda">
      <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-534" data-mp="3b3i2id8i9z72">
        <h2 className="grit-headline text-[clamp(2.5rem,9vw,5rem)] font-extrabold uppercase text-offwhite" data-id="element-535" data-mp="3b1f4b5x78zr9">
          Choose your <span className="text-grit-500" data-id="element-536" data-mp="315c4i6ptld7h">loadout.</span>
        </h2>
        <p className="mt-4 text-lg font-medium text-steel-300 sm:text-xl" data-id="element-537" data-mp="317597ggvhncu">
          Every X1 includes all 3 interchangeable lenses.
        </p>
        <div className="mt-6 border-2 gold-edge bg-[#1a1408] p-5 sm:p-6" data-id="element-538" data-mp="2s4451lbki6bt">
          <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.24em] text-[#f2c94c]" data-id="element-539" data-mp="2s704xf6z752w">
            <InfinityIcon className="h-4 w-4" aria-hidden="true" data-id="element-540" data-mp="2dhf5xkl89v5y" />
            Included with every GritVue X1
          </p>
          <p className="grit-headline mt-2 text-[clamp(1.75rem,5vw,3rem)] font-extrabold uppercase gold-text" data-id="element-541" data-mp="28jvs2lsfzkej">
            Lifetime Limited Warranty
          </p>
          <p className="mt-2 max-w-3xl text-[15px] font-semibold text-steel-200 sm:text-lg" data-id="element-542" data-mp="2ih1n2546fvk1">
            Break it, crack it, scratch a lens. We replace it free, for as long as you own it. You never buy work
            glasses or a headlamp again.
          </p>
        </div>

        <ul className="mt-10 flex flex-col gap-6 lg:grid lg:grid-cols-3 lg:items-start" data-id="element-543" data-mp="2rflw0ejxqqv2">
          {offers.map(offer => {
          const isSelected = selectedOffer === offer.id;
          const qty = quantities[offer.id];
          const math = packMath[offer.id];
          const orderTotal = math.price * qty;
          const orderUnits = math.units * qty;
          return <li key={offer.id} id={`pack-${offer.id}`} className={`h-full scroll-mt-28 ${offer.featured ? '-order-1 lg:order-none' : ''}`} data-id="element-544" data-mp="3a64o7c0ropsg">
                <article className={`relative flex h-full flex-col border bg-ink-950 p-6 transition-colors duration-150 ease-grit lg:p-7 ${isSelected ? 'border-grit-500' : 'border-white/12 hover:border-white/30'} ${offer.featured ? 'lg:-mt-4 lg:pb-9' : ''}`} data-id="element-545" data-mp="1dg0k681kqo4m">
                  {offer.eyebrow && <p className={`mb-3 inline-flex self-start px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${offer.featured ? 'bg-grit-500 text-ink-950' : 'border border-grit-500/60 text-grit-500'}`} data-id="element-546" data-mp="ujsnhsmmnlkx">
                      {offer.eyebrow}
                    </p>}

                  {offer.kicker && <p className="text-xs font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-547" data-mp="2f0xd25kgfife">{offer.kicker}</p>}
                  <h3 className={`mt-1 font-display font-extrabold uppercase leading-none text-offwhite ${offer.featured ? 'text-4xl sm:text-5xl' : 'text-3xl sm:text-4xl'}`} data-id="element-548" data-mp="2k3g1vijal0fp">
                    {offer.name}
                  </h3>
                  <p className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-549" data-mp="26dkw0tfquu67">{offer.units}</p>

                  <div className="mt-6 flex flex-wrap items-end gap-x-3 gap-y-1" data-id="element-550" data-mp="21coqkbfquzyo">
                    <span className={`font-display font-extrabold leading-none text-offwhite ${offer.featured ? 'text-6xl sm:text-7xl' : 'text-5xl'}`} data-id="element-551" data-mp="1wfewgiheu04i">
                      {money(orderTotal)}
                    </span>
                    {math.compareAt && <span className="pb-1 text-base text-steel-500 line-through" data-id="element-552" data-mp="3moyjprgr7g4w">
                        {money(math.compareAt * qty)}
                      </span>}
                  </div>
                  {math.save && <p className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-grit-500" data-id="element-553" data-mp="1b4haylynncyh">
                      You save {money(math.save * qty)}
                    </p>}
                  {offer.perUnit && <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-steel-300" data-id="element-554" data-mp="22ssxnuts4q3u">
                      {offer.perUnit}
                    </p>}
                  {qty > 1 && <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-steel-300" data-id="element-555" data-mp="1yss7xe18cshh">
                      {qty} packs · {orderUnits} X1 total
                    </p>}

                  {offer.highlight && <p className="mt-4 bg-grit-500 px-4 py-3 font-display text-2xl font-extrabold uppercase leading-none tracking-wide text-ink-950 sm:text-3xl" data-id="element-556" data-mp="nfgb3n3w1a4t">
                      {offer.highlight}
                    </p>}
                  {offer.runtime && <p className="mt-3 font-display text-lg font-bold uppercase leading-tight tracking-wide text-offwhite" data-id="element-557" data-mp="otzdwp5t42yx">
                      {offer.runtime}
                    </p>}

                  <div className="mt-5 border-t border-white/10 pt-5" data-id="element-558" data-mp="3scj1vfhgcmnx">
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-559" data-mp="3nd6b4jngmvg2">
                      What&apos;s in the box
                    </p>
                    <ul className="mt-3 grid gap-2 text-[15px] font-medium text-offwhite" data-id="element-560" data-mp="5xzit5hj8jb4">
                      {offer.includes.map(line => {
                    if (/warranty/i.test(line)) {
                      return <li key={line} className="mt-1" data-id="element-561" data-mp="34l5772vtwezs">
                              <Link to="/legal/guarantee" className="group flex items-center gap-4 border gold-edge bg-[#1a1408] p-4 transition-[background-color,box-shadow] duration-150 ease-grit hover:bg-[#241b0b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c]" data-id="element-562" data-mp="2j2vb2045xu3t">
                                <GoldMedal size={86} data-id="element-563" data-mp="2xy1hgo767uxo" />
                                <p className="font-display text-lg font-bold uppercase leading-tight tracking-wide text-[#ffe79a]" data-id="element-564" data-mp="320oer4mm2829">
                                  {math.units > 1 ? `All ${math.units} covered for life.` : 'Covered for life.'}
                                  <span className="mt-0.5 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-565" data-mp="3btvfudabj1wj">
                                    Lifetime Limited Warranty. Break one, we replace it free.
                                  </span>
                                </p>
                              </Link>
                            </li>;
                    }
                    if (/mystery gift/i.test(line)) {
                      return <li key={line} className="mt-1 border border-grit-500/60 bg-grit-500/10 p-3.5 font-display text-lg font-bold uppercase leading-tight tracking-wide text-grit-500" data-id="element-566" data-mp="1530jqzydv2lx">
                              <span className="flex items-center gap-2" data-id="element-567" data-mp="1xtqyxqneo59h">
                                <GiftIcon className="h-4 w-4" aria-hidden="true" data-id="element-568" data-mp="1stcx4qcedrjw" />
                                Free mystery gift
                              </span>
                              <span className="mt-1 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-569" data-mp="1j3tdhigv1tnz">
                                There&apos;s something else in the box. No clues. No extra charge. While supplies
                                last.
                              </span>
                            </li>;
                    }
                    return <li key={line} className="flex items-start gap-2.5" data-id="element-570" data-mp="3qjkdnpkcuher">
                            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-571" data-mp="9adw1lht2y0j" />
                            {line}
                          </li>;
                  })}
                    </ul>
                    {offer.splitNote && <div className="mt-4 border-l-2 border-grit-500 bg-white/[0.03] px-4 py-3" data-id="element-572" data-mp="da6c0j9j46fx">
                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-573" data-mp="rz2f01wqat9o">
                          {offer.splitNote.title}
                        </p>
                        <p className="mt-1 text-[14px] font-medium text-steel-200" data-id="element-574" data-mp="110a907s7p7xy">{offer.splitNote.body}</p>
                        <p className="mt-1 font-display text-base font-bold uppercase tracking-wide text-offwhite" data-id="element-575" data-mp="wzzxygpkbrc8">
                          {offer.splitNote.each}
                        </p>
                      </div>}
                    <PrescriptionBadge className="mt-3" compact data-id="element-576" data-mp="1kkhlcb3lqkhm" />
                    <ul className="mt-4 grid gap-1 border-t border-white/5 pt-4 text-sm text-steel-400" data-id="element-577" data-mp="16lrfa5yt1ah4">
                      {offer.supporting.map(line => <li key={line} data-id="element-578" data-mp="wx9h7whwthh6">{line}</li>)}
                    </ul>
                  </div>

                  <div className="mt-auto pt-7" data-id="element-579" data-mp="11nzj2zr4n274">
                    <QuantityStepper value={qty} onChange={next => handleQuantity(offer.id, next)} label={`${offer.name} quantity`} className="mb-4" data-id="element-580" data-mp="11psuescoqalg" />
                    <button id={`pack-${offer.id}-cta`} type="button" onClick={() => {
                  onSelectOffer(offer.id);
                  trackCustom(packEvents[offer.id].addToCart, {
                    quantity: qty,
                    value: orderTotal
                  });
                }} className={`flex w-full scroll-mb-28 items-center justify-center gap-2 px-5 py-4 text-center font-display font-extrabold uppercase tracking-wide transition-[background-color,color,transform] duration-150 ease-grit active:translate-y-[2px] ${offer.featured ? 'bg-grit-500 text-ink-950 hover:bg-grit-400 text-2xl' : 'border-2 border-grit-500 text-grit-500 hover:bg-grit-500 hover:text-ink-950 text-xl'}`} data-id="element-581" data-mp="dz6p38u2f4f5">
                      {qty > 1 ? `Get ${orderUnits} X1 for ${money(orderTotal)}` : offer.cta}
                    </button>
                    <UspsBadge className="mt-3" note={offer.note} data-id="element-582" data-mp="1j67uvttt20aq" />
                    <SecureCheckoutNote className="mt-4" data-id="element-583" data-mp="1e5sj36p690uv" />
                    {offer.badge && <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-grit-500" data-id="element-584" data-mp="142wzc7dnnar1">
                        {offer.badge}
                      </p>}
                    {offer.footnote && <p className="mt-3 text-[11px] leading-relaxed text-steel-500" data-id="element-585" data-mp="1dzsy3ldr4iie">{offer.footnote}</p>}
                  </div>
                </article>
              </li>;
        })}
        </ul>

        <IncludedGear data-id="element-586" data-mp="1muz8kxnyvor9" />

        <CompanyPack data-id="element-587" data-mp="1rrzgle4dn1l0" />

        <FreeUnitModal open={freeUnitOpen} onAccept={acceptFreeUnit} onDecline={declineFreeUnit} data-id="element-588" data-mp="1rr6e4pcgay8o" />

        <div className="mt-10 flex flex-col gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-589" data-mp="1wqsu36jnysl6">
          <div data-id="element-590" data-mp="21pnbq55wab3p">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-591" data-mp="21o2pd2tqhygx">Secure checkout</p>
            <div className="mt-2" data-id="element-592" data-mp="2fe36mex2n3u">
              <PaymentLogos data-id="element-593" data-mp="7bckxr5qp6va" />
            </div>
          </div>
          <p className="max-w-md text-[13px] text-steel-400" data-id="element-594" data-mp="7dz28iifn0qp">
            30-day returns with free return shipping · Lifetime free replacement ·{' '}
            <Link to="/legal/shipping" className="font-semibold text-grit-500 hover:underline" data-id="element-595" data-mp="1st3m5cakpwd1">
              Shipping &amp; returns
            </Link>
          </p>
        </div>
      </div>
    </section>;
}